import { useState } from "react";
import { Trash2, Circle, CheckCheck } from "lucide-react";
import { api } from "../../lib/api";

// COLLECTION_MAP: the frontend key → the URL param used by the backend generic admin endpoints
// See /app/backend/server.py ADMIN_LEAD_COLLECTIONS
export const LEAD_COLLECTIONS = {
  intakes: "intakes",
  consultations: "consultations",
  insurance: "insurance-inquiries",
  insuranceConsultation: "insurance-consultations",
  education: "education-inquiries",
};

// Small "NEW" pill shown on unread rows
export const NewPill = () => (
  <span
    data-testid="lead-new-pill"
    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#C89B3C] text-white px-2 py-0.5"
  >
    <Circle size={6} className="fill-current" /> New
  </span>
);

// Row actions: mark-read/unread toggle + delete-with-confirm.
// `stopPropagation` on all handlers so clicking these doesn't open the parent row's detail modal.
export const LeadRowActions = ({ collection, recordId, isRead, onChanged }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleRead = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/lead/${collection}/${recordId}/read`, { isRead: !isRead });
      onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update");
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete(`/admin/lead/${collection}/${recordId}`);
      setConfirming(false);
      onChanged();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={toggleRead}
        disabled={busy}
        data-testid={`lead-toggle-read-${recordId}`}
        title={isRead ? "Mark as unread" : "Mark as read"}
        className="text-slate-400 hover:text-[#0B2343] p-1.5 transition-colors disabled:opacity-40"
      >
        <CheckCheck size={14} className={isRead ? "" : "text-[#C89B3C]"} />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setConfirming(true);
        }}
        disabled={busy}
        data-testid={`lead-delete-${recordId}`}
        title="Delete record"
        className="text-slate-400 hover:text-red-600 p-1.5 transition-colors disabled:opacity-40"
      >
        <Trash2 size={14} />
      </button>

      {confirming && (
        <ConfirmDeleteModal
          onCancel={() => setConfirming(false)}
          onConfirm={doDelete}
          busy={busy}
        />
      )}
    </div>
  );
};

const ConfirmDeleteModal = ({ onCancel, onConfirm, busy }) => (
  <div
    className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
    onClick={(e) => {
      e.stopPropagation();
      onCancel();
    }}
  >
    <div
      className="bg-white w-full max-w-sm p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="font-serif text-lg font-bold text-[#0B2343] mb-2">Delete this record?</h3>
      <p className="text-sm text-slate-600 mb-6">
        This cannot be undone. The record and all associated data will be permanently removed.
      </p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCancel(); }}
          className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={busy}
          data-testid="lead-delete-confirm"
          className="bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          {busy ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

// helper to check if a record is "new" (strict false — missing/undefined counts as read)
export const isRecordNew = (r) => r?.isRead === false;

// helper to build the class for row highlight when unread
export const unreadRowClass = (r) =>
  isRecordNew(r) ? "bg-[#FFF9EC] border-l-4 border-l-[#C89B3C]" : "";

// helper to fire the auto-mark-read PATCH when a record is opened for detail view.
// Fire-and-forget; caller doesn't need to await.
export const markRead = async (collection, recordId) => {
  try {
    await api.patch(`/admin/lead/${collection}/${recordId}/read`, { isRead: true });
  } catch (e) {
    // best-effort — non-blocking
  }
};
